<template>
    <v-app>
        <Loader />

        <v-main>
            <template v-if="$route.meta.requiresAuth">
                <Menu />
                <Toolbar />
                <v-container fluid>
                    <transition name="slide-x-transition" mode="out-in">
                        <router-view :key="$route.name"></router-view>
                    </transition>
                </v-container>
            </template>
            <template v-else>
                <transition name="slide-x-transition" mode="out-in">
                    <router-view :key="$route.name"></router-view>
                </transition>
            </template>
        </v-main>

        <v-btn
            v-if="canUseAssistant && !chatOpen && $route.name !== 'login' && $route.name !== 'logout' && $route.name !== 'Validate'"
            color="var(--color__main)"
            dark
            fab
            fixed
            bottom
            right
            class="mt-4"
            style="z-index: 2000; margin-right: 80px !important;"
            @click="chatOpen = !chatOpen"
        >
            <v-icon>mdi-chat</v-icon>
        </v-btn>

        <v-navigation-drawer
            v-if="canUseAssistant"
            v-model="chatOpen"
            right
            temporary
            app
            width="420"
            :style="{ background: 'linear-gradient(180deg, #010D1B 0%, #021D30 100%)', zIndex: 2001 }"
        >
            <v-card flat dark class="d-flex flex-column fill-height" style="background: transparent;">
                <v-card-title class="d-flex align-center justify-space-between px-4 py-3 white--text">
                    <span>VK360 Assistant</span>
                    <v-btn icon dark @click="chatOpen = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-divider dark></v-divider>

                <v-card-text class="flex-grow-1 pa-3 chat-scroll">
                    <div v-for="(message, index) in messages" :key="index" class="mb-3" >
                        <div :class="message.role === 'user' ? 'text-right' : 'text-left'">
                            <v-chip
                                class="assistant-message pa-3"
                                :color="message.role === 'user' ? 'var(--color__main)' : '#071D2B'"
                                :text-color="message.role === 'user' ? 'white' : 'white'"
                                style="white-space: pre-wrap; max-width: 100%; border-radius: 14px; height: auto;"
                            >
                                {{ message.content }}
                            </v-chip>
                        </div>
                    </div>
                </v-card-text>

                <v-divider dark></v-divider>

                <v-card-actions class="pa-3">
					<div style="width: 100%; text-align: center; display: flex;">
						<v-text-field
							v-model="input"
							label="Ask a question"
							dark
							dense
							outlined
							maxlength="4000"
							:disabled="isSending"
							@keyup.enter="sendMessage"
						></v-text-field>
						<v-btn 
							style="margin-left: 10px; height: 40px;"
							color="var(--color__main)" 
							dark 
							:loading="isSending"
							:disabled="isSending"
							@click="sendMessage"
						>
							Send
						</v-btn>
					</div>
                </v-card-actions>
            </v-card>
        </v-navigation-drawer>

        <ModalToDo />
    </v-app>
</template>

<script>

    import Vue from 'vue';
    import Loader from '@/components/Layout/Loader';
    import ModalToDo from '@/components/Layout/ModalToDo';
    import Menu from "@/views/Main/Menu";
    import Toolbar from "@/views/Main/Toolbar";
    import ReadOnlyAccess from '@/utilities/ReadOnlyAccess';

    const assistantGreeting = 'Hello! I can answer questions about VK360, look up jobs and customer contacts, list pending work orders and inspections, and open screens available to your profile.';

    export default {
        name: 'App',

        mixins: [ReadOnlyAccess],

        components: {
            Loader,
            ModalToDo,
            Menu,
            Toolbar
        },

        data: () => ({
            chatOpen: false,
            input: '',
            isSending: false,
            assistantSession: 0,
            messages: [
                {
                    role: 'assistant',
                    content: assistantGreeting
                }
            ]
        }),

        computed: {
            canUseAssistant() {
                const profile = this.$store.getters['logInModule/searchState']?.profile;
                return this.$root.isAuthenticated && profile?.readOnlyAccess != null && !this.isReadOnlyAccess;
            },

            assistantUserId() {
                return this.$store.getters['logInModule/searchState'].id;
            }
        },

        watch: {
            canUseAssistant(allowed) {
                if (!allowed) this.resetAssistant();
            },
            assistantUserId() {
                this.resetAssistant();
            },
            $route(route) {
                if (!route.meta.requiresAuth) this.resetAssistant();
            }
        },

        methods: {
            resetAssistant() {
                this.assistantSession++;
                this.chatOpen = false;
                this.input = '';
                this.isSending = false;
                this.messages = [{ role: 'assistant', content: assistantGreeting }];
            },

            getAvailableScreens() {
                return this.$router.getRoutes()
                    .filter(route => route.name && route.meta && route.meta.requiresAuth)
                    .map(route => ({
                        id: String(route.name),
                        path: route.path
                    }));
            },

            async navigateToScreen(navigation) {
                if (!navigation || !navigation.screenId) return false;

                const screen = this.getAvailableScreens().find(item => item.id === navigation.screenId);
                if (!screen) return false;

                const route = this.$router.getRoutes().find(item => String(item.name) === screen.id);
                if (!route || !route.meta || !route.meta.requiresAuth) return false;

                const params = navigation.params && typeof navigation.params === 'object'
                    ? navigation.params
                    : {};
                const routeParams = (route.path.match(/:[A-Za-z0-9_]+/g) || [])
                    .map(param => param.substring(1));
                const requiredParams = routeParams.filter(param => {
                    const parameterToken = ':' + param;
                    return route.path.charAt(route.path.indexOf(parameterToken) + parameterToken.length) !== '?';
                });
                const missingParams = requiredParams.filter(param => !params[param]);

                if (missingParams.length > 0) {
                    this.messages.push({
                        role: 'assistant',
                        content: 'I need more information to open that screen: ' + missingParams.join(', ') + '.'
                    });
                    return true;
                }

                const validParams = {};
                routeParams.forEach(param => {
                    if (params[param] !== undefined && params[param] !== null) {
                        validParams[param] = String(params[param]).substring(0, 100);
                    }
                });

                try {
                    await this.$router.push({ name: route.name, params: validParams });
                } catch (error) {
                    if (!error || error.name !== 'NavigationDuplicated') {
                        throw error;
                    }
                }

                return true;
            },

            async sendMessage() {
                if (!this.canUseAssistant || this.isSending || !this.input || !this.input.trim()) return;

                const session = this.assistantSession;
                const question = this.input.trim();
                this.messages.push({ role: 'user', content: question });
                this.input = '';
                this.isSending = true;

                try {
                    const response = await this.$store.dispatch('assistantModule/Chat', {
                        message: question,
                        history: this.messages.slice(-11, -1).map(message => ({
                            role: message.role,
                            content: message.query ? null : message.content,
                            query: message.query
                        })),
                        availableScreens: this.getAvailableScreens()
                    });
                    if (session !== this.assistantSession) return;

                    if (response && response.success === false) {
                        this.messages.push({ role: 'assistant', content: response.message || 'I could not get a response.' });
                        return;
                    }

                    if (!response || typeof response.text !== 'string') {
                        this.messages.push({ role: 'assistant', content: 'The assistant returned an invalid response. Please try again.' });
                        return;
                    }

                    this.messages.push({ role: 'assistant', content: response.text, query: response.query });
                    if (response.navigation) {
                        const navigated = await this.navigateToScreen(response.navigation);
                        if (!navigated) {
                            this.messages.push({
                                role: 'assistant',
                                content: 'I could not identify an available screen to open. Could you specify the screen name?'
                            });
                        }
                    }
                }
                catch (error) {
                    if (session !== this.assistantSession) return;
                    this.messages.push({ role: 'assistant', content: 'There was a problem contacting the assistant. Please try again.' });
                }
                finally {
                    if (session === this.assistantSession) this.isSending = false;
                }
            }
        },

        created() {
            Vue.prototype.getApp = this;
            Vue.prototype.getToast = this.$toast;
            Vue.prototype.getDialog = this.$dialog;
            Vue.prototype.getRouter = this.$router;
            Vue.prototype.getRoot = this.$root;
        },
    };
</script>

<style>

    .copyright {
        direction: rtl;
        font-size: small;
        background-color: var(--color__main);
        color:white;
        height: 30px;
        line-height: 30px;
        margin-bottom: 0px !important;
        text-align: center;
    }

    .chat-scroll {
        overflow-y: auto;
    }

    .assistant-message .v-chip__content {
        display: block;
        white-space: pre-wrap;
        overflow-wrap: anywhere;
    }

</style>